import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DataTable } from '../../../../../shared/components/common/data-table/DataTable';
import { Column } from 'primereact/column';
import { Skeleton } from 'primereact/skeleton';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import './DomainSourcesTable.scss';
import { DomainSourceStats } from '../../../../../shared/services/monitor/data/sources/MonitorDataSourcesService';
import { SearchFilter } from '../../../../../shared/components/search-filter/SearchFilter';
import CustomTooltip from '../../../../../shared/components/common/tooltip/CustomTooltip';
import EmptyPanel from '../../../../../shared/components/empty-panel/EmptyPanel';
import { MetadataBadge } from '../../../../../shared/components/metadata-badge/MetadataBadge';
import { PageTypeTag } from '../../../../../shared/components/page-type-tag/PageTypeTag';
import { getVerifiedBrandStatus, isFirmMentionedInBrands } from '../../../helpers/SourcesHelper';
import { PaginationEvent } from '../../../../../shared/helpers/PaginationHelper';
import KeywordTags from '../../../../../shared/components/keyword-tags/KeywordTags';
import { TrendTag } from '../../../../projects/preview/components/trend-tag/TrendTag';
import { getTrendValue } from '../../../../../shared/utils/utils';
import { PROJECTS, QUESTIONS, SOURCES } from '../../../../../shared/routes/path';
import { encodeSourceHash } from '../../../../../shared/utils/urls';
import { KeywordsData, TagsResponse } from '../../../../../shared/models/tags/TagsResponse';

interface DomainSourcesPagination {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

export type DomainSourceStatsWithComparison = DomainSourceStats & {
  previousShare: number;
  shareChange: number;
};

export interface DomainSourcesTableProps {
  sources: DomainSourceStatsWithComparison[];
  pagination?: DomainSourcesPagination;
  onPageChange: (page: number, pageSize: number) => void;
  isLoading: boolean;
  keyword: string;
  onKeywordChange: (keyword: string) => void;
  firmName: string;
  tags?: TagsResponse[];
  citationGapsActive: boolean;
  citationGapsCount: number;
  onCitationGapsToggle: () => void;
  projectId: number;
  monitorPeriod: string | null;
  selectedChatType: string | null;
  hasSourceDetailPage: boolean;
  commonKeywords: KeywordsData[];
  sortBy?: string;
  sortOrder?: string;
  onSort?: (field: string, order: string) => void;
  hasAdvancedFeatures?: boolean;
}

export const DomainSourcesTable: React.FC<DomainSourcesTableProps> = ({
  sources,
  pagination,
  onPageChange,
  isLoading,
  keyword,
  onKeywordChange,
  firmName,
  tags = [],
  citationGapsActive,
  citationGapsCount,
  onCitationGapsToggle,
  projectId,
  monitorPeriod,
  selectedChatType,
  hasSourceDetailPage,
  commonKeywords,
  sortBy,
  sortOrder,
  onSort,
  hasAdvancedFeatures = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedRows, setExpandedRows] = useState<any>(null);

  const brandsMap = useMemo(() => {
    const map = new Map<string, string>();
    commonKeywords.forEach((k) => map.set(k.normalizedKeyword, k.keyword));
    return map;
  }, [commonKeywords]);

  const navigateToSource = (source: string) => {
    const hash = encodeSourceHash(source);
    const period = monitorPeriod?.toLowerCase() || 'daily';
    const chat = selectedChatType?.toLowerCase() || 'all';
    navigate(`${SOURCES}/${projectId}/source/${hash}/${period}/${chat}${location.search}`);
  };

  const first = pagination ? (pagination.page - 1) * pagination.pageSize : 0;
  const rows = pagination?.pageSize ?? 10;
  const totalRecords = pagination?.totalItems ?? sources.length;

  const safeFirst = useMemo(() => {
    if (totalRecords === 0) return 0;
    const maxFirst = Math.floor((totalRecords - 1) / rows) * rows;
    return Math.max(0, Math.min(first, maxFirst));
  }, [first, rows, totalRecords]);

  const primeReactSortOrder = sortOrder === 'ASC' ? 1 : -1;

  const handleSort = (e: any): void => {
    if (!onSort) return;
    const field = e.sortField;
    const order = e.sortOrder === 1 ? 'ASC' : 'DESC';
    onSort(field, order);
  };

  const handlePageChange = (event: PaginationEvent): void => {
    const newPage = Math.floor(event.first / event.rows) + 1;
    const newPageSize = event.rows;
    if (pagination) {
      if (newPage !== pagination.page || newPageSize !== pagination.pageSize) {
        onPageChange(newPage, newPageSize);
      }
    } else {
      onPageChange(newPage, newPageSize);
    }
  };

  const rowExpansionTemplate = (rowData: DomainSourceStats) => (
    <div className="domain-sources-table__expansion">
      <div className="domain-sources-table__expansion-title">{t('promptsUsingThisUrl')}</div>
      {rowData.prompts.map((prompt, index) => {
        const chatDate = prompt.createdAt ? format(new Date(prompt.createdAt), 'yyyy-MM-dd') : '';
        return (
          <a
            key={index}
            className="domain-sources-table__prompt-box domain-sources-table__prompt-box--clickable"
            onClick={() => {
              navigate(
                `${PROJECTS}/${projectId}${QUESTIONS}/${prompt.monitorPromptId}/daily/all?chatDate=${chatDate}&chatId=${prompt.monitorDataId}`
              );
            }}
          >
            <div className="domain-sources-table__prompt-content">
              <span className="domain-sources-table__prompt-keyword">{prompt.keyword}</span>
              {prompt.snippet && (
                <span className="domain-sources-table__prompt-snippet">{prompt.snippet}</span>
              )}
            </div>
            <span className="domain-sources-table__prompt-date">{chatDate}</span>
          </a>
        );
      })}
    </div>
  );

  return (
    <div className="stats__panel domain-sources-table">
      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">
          {t('domainSourcesAnalytics')}
          <CustomTooltip tooltipText="domainSourcesAnalyticsTooltip" addMargin={false} />
        </div>
        <div className="page-box__source-filter">
          {hasAdvancedFeatures && citationGapsCount > 0 && (
            <button
              type="button"
              className={`domain-sources-table__citation-gaps-btn${citationGapsActive ? ' domain-sources-table__citation-gaps-btn--active' : ''}`}
              onClick={onCitationGapsToggle}
            >
              {t('citationGaps')}
              <span className="domain-sources-table__citation-gaps-badge">{citationGapsCount > 9 ? '9+' : citationGapsCount}</span>
            </button>
          )}
          <SearchFilter value={keyword} onChange={onKeywordChange} className="search-filter--inline" />
        </div>
      </div>

      {isLoading ? (
        <Skeleton className="skeleton--table">
          <span />
          <span />
          <span />
          <span />
          <span />
          <span />
          <span />
        </Skeleton>
      ) : (
        <DataTable
          value={sources}
          stripedRows
          tableClassName="table"
          className="table-wrapper table-wrapper--stack"

          responsiveLayout="stack"
          tableStyle={{ width: '100%' }}
          paginator
          lazy
          emptyMessage={() => <EmptyPanel />}
          paginatorClassName="pagination"
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          rows={rows}
          first={safeFirst}
          onPage={handlePageChange}
          totalRecords={totalRecords}
          rowsPerPageOptions={[10, 25, 50]}
          dataKey="source"
          loading={isLoading}
          currentPageReportTemplate={t('rowsNumber') + ':'}
          expandedRows={expandedRows}
          onRowToggle={(e) => setExpandedRows(e.data)}
          rowExpansionTemplate={rowExpansionTemplate}
          sortField={sortBy}
          sortOrder={primeReactSortOrder}
          onSort={handleSort}
        >
          <Column
            field="source"
            header={t('source')}
            bodyClassName="table__cell--stack-title"
            headerClassName="table__header"
            style={{ minWidth: '280px', maxWidth: '280px', width: 'auto' }}
            body={(rowData: DomainSourceStats, options: any) => (
              <div className="source-data">
                <span
                  className="source-data__icon"
                  style={{ backgroundImage: `url("https://favicon.is/${rowData.domain}")` }}
                />
                <div className="source-data__details">
                  {rowData.title && (
                    hasSourceDetailPage ? (
                      <a
                        className="source-data__title source-data__title--clickable text-ellipsis"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigateToSource(rowData.source);
                        }}
                      >
                        {rowData.title}
                      </a>
                    ) : (
                      <span className="source-data__title text-ellipsis">{rowData.title}</span>
                    )
                  )}
                  <span className="source-data__url text-ellipsis">
                    {hasSourceDetailPage ? (
                      <span className="source-data__source-with-link">
                        <a
                          className="custom-link custom-link--clickable text-ellipsis"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigateToSource(rowData.source);
                          }}
                        >
                          {rowData.source}
                        </a>
                        <a
                          className="source-data__external-link"
                          href={rowData.source.startsWith('http') ? rowData.source : `https://${rowData.source}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <span className="ico--sm ico--external-link" />
                        </a>
                      </span>
                    ) : (
                      <a
                        className="custom-link text-ellipsis"
                        href={rowData.source.startsWith('http') ? rowData.source : `https://${rowData.source}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {rowData.source}
                      </a>
                    )}
                  </span>
                  {hasAdvancedFeatures && (
                    <KeywordTags
                      keywordsString={rowData.brands}
                      keywordsMap={brandsMap}
                      containerWidth={260}
                      minVisibleKeywords={3}
                      uniqueId={`domain-source-${options.rowIndex}`}
                    />
                  )}
                </div>
              </div>
            )}
          />

          <Column
            field="pageType"
            header={t('urlType')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            style={{ width: '100px', minWidth: '100px' }}
            body={(rowData: DomainSourceStats) => <PageTypeTag pageType={rowData.pageType} />}
          />

          {hasAdvancedFeatures && (
            <Column
              field="mentioned"
              header={t('mentioned')}
              sortable
              headerClassName="table__header table__header--center"
              bodyClassName="table__cell--center"
              style={{ width: '80px', minWidth: '80px' }}
              body={(rowData: DomainSourceStats, options: any) => {
                const { variant, statusText, tooltipText } = getVerifiedBrandStatus(
                  rowData.isVerified,
                  isFirmMentionedInBrands(rowData.brands, firmName, tags),
                  firmName,
                  t
                );
                return (
                  <MetadataBadge
                    variant={variant}
                    text={statusText}
                    tooltipText={tooltipText}
                    uniqueId={`domain-source-brand-tooltip-${options.rowIndex}`}
                  />
                );
              }}
            />
          )}

          <Column
            field="count"
            header={t('usageCount')}
            sortable
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            style={{ width: '80px', minWidth: '80px' }}
            body={(rowData: DomainSourceStats) => rowData.count}
          />

          <Column
            field="share"
            header={t('sourcesAnalyticsShare')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            style={{ width: '70px', minWidth: '70px' }}
            body={(rowData: DomainSourceStatsWithComparison) => `${rowData.share.toFixed(1)}%`}
          />

          <Column
            header={t('sourcesAnalyticsShareChange')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            style={{ width: '65px', minWidth: '65px' }}
            body={(rowData: DomainSourceStatsWithComparison) => (
              <TrendTag trendValue={getTrendValue(rowData.shareChange, 2)} suffix="%" />
            )}
          />

          <Column
            field="firstSeen"
            header={t('firstSeen')}
            sortable
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            style={{ width: '130px', minWidth: '130px' }}
            body={(rowData: DomainSourceStats) =>
              format(new Date(rowData.firstSeen), 'yyyy-MM-dd')
            }
          />

          <Column
            field="prompts"
            header={t('sourcesAnalyticsPrompts')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            style={{ width: '70px', minWidth: '70px' }}
            body={(rowData: DomainSourceStats) => rowData.promptCount}
          />

          <Column
            expander={true}
            bodyClassName="table__cell--stack-actions"
            style={{ width: '25px' }}
            frozen={true}
            alignFrozen="right"
          />
        </DataTable>
      )}
    </div>
  );
};

export default DomainSourcesTable;
